import React, { type ReactNode } from 'react'
import { DataTable, DataTableRow, Skeleton, colors } from '@fs/zion-ui'
import { css } from '@emotion/core'
import SourceColumnHeaderSkeleton from './SourceColumnHeaderSkeleton'
import SourceGridLabelCellSkeleton from './SourceGridLabelCellSkeleton'
import SourceCellSkeleton from './SourceCellSkeleton'

const ROW_IDS = ['row-1', 'row-2', 'row-3', 'row-4', 'row-5', 'row-6', 'row-7'] as const

// 8 source columns. Keys are descriptive to avoid array-index keys in JSX.
const SOURCE_COL_IDS = [
  'source-a',
  'source-b',
  'source-c',
  'source-d',
  'source-e',
  'source-f',
  'source-g',
  'source-h',
] as const

const containerCss = css`
  border: 1px solid ${colors.gray10};
  border-radius: 4px;
  overflow: hidden;
  td,
  th {
    border-inline-end: 1px solid ${colors.gray10};
    &:last-child {
      border-inline-end: none;
    }
  }
  tr > [sticky-col][last-sticky-col] {
    border-inline-end-color: ${colors.gray10};
  }
  tbody td {
    min-height: 80px;
  }
`

function makeRowCells(): Record<string, ReactNode> {
  const cells: Record<string, ReactNode> = { '0': <SourceGridLabelCellSkeleton /> }
  SOURCE_COL_IDS.forEach((id, i) => {
    cells[String(i + 1)] = <SourceCellSkeleton key={id} />
  })
  return cells
}

export default function SourceGridSkeleton(): JSX.Element {
  const columns = [
    { label: <Skeleton.H5 aria-hidden />, size: 'md' as const },
    ...SOURCE_COL_IDS.map((id) => ({
      label: <SourceColumnHeaderSkeleton key={id} />,
      size: 'md' as const,
    })),
  ]

  return (
    <div css={containerCss} aria-busy aria-label="Loading source comparison grid">
      <DataTable columns={columns} stickyFirstColumn={1} verticalAlign="top" gutters="sm">
        {ROW_IDS.map((id) => (
          <DataTableRow key={id} {...(makeRowCells() as Record<string, ReactNode>)} />
        ))}
      </DataTable>
    </div>
  )
}
